import React from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Paginator } from "primereact/paginator";
import agent from "../../../agent";
import { MILE_TYPES, mileLabel } from "./ladderAdapters";
import LadderEditor from "./LadderEditor";

const PAGE_SIZE = 20;

/**
 * Администрирование тарифов нового калькулятора.
 *
 * Навигация сверху вниз: перевозчик и услуга, поиск маршрута, выбор ноги,
 * редактор её весовой лестницы. Плоским списком тут не обойтись — ног больше
 * одиннадцати тысяч, а весовых диапазонов свыше трёхсот пятидесяти тысяч,
 * поэтому поиск идёт на стороне сервера.
 */
const CarrierTariffAdmin = () => {
  const [carriers, setCarriers] = React.useState([]);
  const [carrier, setCarrier] = React.useState(null);
  const [miles, setMiles] = React.useState([]);
  const [origin, setOrigin] = React.useState("");
  const [destination, setDestination] = React.useState("");

  const [legs, setLegs] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [first, setFirst] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [searched, setSearched] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [selected, setSelected] = React.useState(null);

  React.useEffect(() => {
    agent.OrgTariffs.postalServices()
      .then((options) =>
        setCarriers(
          (options ?? []).map((option) => ({
            ...option,
            key: `${option.psCode}|${option.psTypeCode}`,
            label: `${option.psCode} — ${option.psTypeCode}${option.name ? ` (${option.name})` : ""}`,
          }))
        )
      )
      .catch(() => setError("Не удалось загрузить список перевозчиков"));
  }, []);

  const search = (offset = 0) => {
    setLoading(true);
    setError(null);
    setSelected(null);
    agent.CarrierDeliveryLegs.search({
      psId: carrier?.psId,
      mileTypes: miles,
      originQuery: origin.trim(),
      destinationQuery: destination.trim(),
      page: offset / PAGE_SIZE,
      size: PAGE_SIZE,
    })
      .then((page) => {
        setLegs(page?.content ?? []);
        setTotal(page?.totalElements ?? 0);
        setFirst(offset);
        setSearched(true);
      })
      .catch((e) => setError(readError(e, "Поиск не выполнен")))
      .finally(() => setLoading(false));
  };

  const afterDeletion = (leg) => {
    setSelected(null);
    setLegs((current) => current.filter((item) => item.id !== leg.legId));
    setTotal((current) => Math.max(0, current - 1));
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <h2 style={{ marginTop: 0 }}>Администрирование тарифов — новый калькулятор</h2>
      <Message
        severity="info"
        text="Тарифы маршрутные: цена привязана к паре КАТО и миле. Старый экран «Администрирование тарифов» работает с зонами прежнего калькулятора и остаётся на месте."
        style={{ display: "block", marginBottom: 16 }}
      />

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16 }}>
        <label style={{ display: "grid", gap: 6, minWidth: 280 }}>
          <span>Перевозчик и услуга</span>
          <Dropdown value={carrier} options={carriers} optionLabel="label" dataKey="key"
                    filter showClear placeholder="Любой" onChange={(e) => setCarrier(e.value)} />
        </label>

        <label style={{ display: "grid", gap: 6, minWidth: 220 }}>
          <span>Миля</span>
          <MultiSelect value={miles} options={MILE_TYPES} optionLabel="label" optionValue="value"
                       placeholder="Все" onChange={(e) => setMiles(e.value)} display="chip" />
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <span>Откуда</span>
          <InputText value={origin} placeholder="город или КАТО" aria-label="Откуда"
                     onChange={(e) => setOrigin(e.target.value)} />
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <span>Куда</span>
          <InputText value={destination} placeholder="город или КАТО" aria-label="Куда"
                     onChange={(e) => setDestination(e.target.value)} />
        </label>

        <Button label="Найти" icon="pi pi-search" onClick={() => search(0)} disabled={loading} />
      </div>

      {error && <Message severity="error" text={error} style={{ display: "block", marginBottom: 16 }} />}

      {loading && <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><ProgressSpinner /></div>}

      {searched && !loading && legs.length === 0 && (
        <Message severity="warn" text="Ничего не нашлось. Попробуйте расширить фильтры." style={{ display: "block" }} />
      )}

      {legs.length > 0 && !loading && (
        <>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.92rem" }}>
              <thead>
                <tr>
                  <th style={head}>Перевозчик</th>
                  <th style={head}>Миля</th>
                  <th style={head}>Откуда</th>
                  <th style={head}>Куда</th>
                  <th style={{ ...head, textAlign: "right" }}>Точек</th>
                  <th style={head}>Срок</th>
                  <th style={head} aria-label="Действия" />
                </tr>
              </thead>
              <tbody>
                {legs.map((leg) => (
                  <tr key={leg.id} style={selected?.legId === leg.id ? { background: "#eff6ff" } : undefined}>
                    <td style={cell}>{leg.psCode} {leg.productCode}</td>
                    <td style={cell}>{mileLabel(leg.mileType)}</td>
                    <td style={cell}>{leg.originCity ?? leg.originKato}</td>
                    <td style={cell}>{leg.destinationCity ?? leg.destinationKato}</td>
                    <td style={{ ...cell, textAlign: "right", fontVariantNumeric: "tabular-nums",
                                 color: leg.ladderPoints === 0 ? "#b91c1c" : undefined,
                                 fontWeight: leg.ladderPoints === 0 ? 600 : undefined }}>
                      {leg.ladderPoints}
                    </td>
                    <td style={cell}>{leg.minDay}–{leg.maxDay} дн.</td>
                    <td style={cell}>
                      <Button label="Лестница" size="small" outlined
                              onClick={() => setSelected({ ...leg, legId: leg.id })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Paginator first={first} rows={PAGE_SIZE} totalRecords={total}
                     onPageChange={(e) => search(e.first)} />
        </>
      )}

      {selected && (
        <LadderEditor
          leg={selected}
          onClose={() => setSelected(null)}
          onDeleted={afterDeletion}
        />
      )}
    </div>
  );
};

const head = {
  textAlign: "left",
  padding: "6px 10px",
  borderBottom: "1px solid #d1d5db",
  fontSize: "0.75rem",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "#6b7280",
  whiteSpace: "nowrap",
};

const cell = { padding: "6px 10px", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" };

const readError = (e, fallback) =>
  e?.response?.body?.message || e?.response?.body?.error || e?.message || fallback;

export default CarrierTariffAdmin;
